//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentationStyles } from '../../styles/Documentation';
import LeftSection from './LeftSection';
import RightSidebar from './RightSidebar';

const SIDEBAR_OFFSET = 62;
const SIDEBAR_OFFSET_RIGHT = 25;

function Documentation() {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(window.devicePixelRatio);
  const leftSectionRef = useRef<HTMLDivElement>(null);
  const [leftHeight, setLeftHeight] = useState<number>(0);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  useEffect(() => {
    const updateHeight = () => {
      if (leftSectionRef.current) {
        setLeftHeight(leftSectionRef.current.offsetHeight);
      }
    };
    updateHeight();
    const resizeObserver = new window.ResizeObserver(updateHeight);
    if (leftSectionRef.current) resizeObserver.observe(leftSectionRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  useEffect(() => {
    const handleZoom = () => {
      setZoom(window.devicePixelRatio);
    };
    window.addEventListener('resize', handleZoom);
    return () => window.removeEventListener('resize', handleZoom);
  }, []);

  return (
    <div
      id="id-docs-page-container"
      data-testid="docs-page-container"
      style={{
        ...documentationStyles.pageContainer,
        flexDirection: 'column',
      }}
    >
      {/* Main flex container */}
      <div
        id="id-docs-main-container"
        data-testid="docs-main-container"
        style={{
          ...documentationStyles.mainContainer,
          flexDirection: 'row',
          // 120px = navbar (48px) + breadcrumb (50px) + padding/margins (22px)
          height: 'calc(100vh - 120px)',
          minHeight: 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Left Section */}
        <div
          ref={leftSectionRef}
          id="doc-left-section"
          data-testid="docs-left-section"
          style={{
            ...documentationStyles.leftSection.outerContainer,
            height: '100%',
          }}
        >
          <LeftSection zoom={zoom} sidebarExpanded={sidebarExpanded} />
        </div>

        {/* Right Sidebar */}
        <div
          id="id-docs-right-sidebar"
          data-testid="docs-right-sidebar"
          style={{
            ...documentationStyles.rightSidebar.outerContainer,
            height: leftHeight
              ? `${leftHeight - SIDEBAR_OFFSET_RIGHT}px`
              : '100%',
          }}
        >
          <RightSidebar
            zoom={zoom}
            leftHeight={leftHeight - SIDEBAR_OFFSET}
            onExpandChange={setSidebarExpanded}
          />
        </div>
      </div>
    </div>
  );
}

export default Documentation;
